import moment from "moment";

const API_FORMAT = moment.HTML5_FMT.DATE; // "YYYY-MM-DD"

export type ExportDatePreset =
  | "since_opening"
  | "today"
  | "past_24_hours"
  | "past_7_days"
  | "past_30_days"
  | "past_90_days"
  | "past_year"
  | "present"
  | "custom";

export interface ExportDateFieldValue {
  preset: ExportDatePreset;
  /** ISO (YYYY-MM-DD) value from the native date input when preset === "custom". */
  customDate: string;
}

export interface DateDropdownOption {
  value: ExportDatePreset;
  /** Label shown in the dropdown list. */
  label: string;
  /** Label shown on the collapsed control (defaults to label). */
  selectedLabel?: string;
}

export const FROM_DATE_OPTIONS: DateDropdownOption[] = [
  { value: "today", label: "Today" },
  { value: "past_24_hours", label: "Past 24 hours" },
  { value: "past_7_days", label: "Past 7 days" },
  { value: "past_30_days", label: "Past 30 days" },
  { value: "past_90_days", label: "Past 90 days" },
  { value: "past_year", label: "Past year" },
  {
    value: "since_opening",
    label: "Since account opening",
    selectedLabel: "Account Open",
  },
];

export const TO_DATE_OPTIONS: DateDropdownOption[] = [
  { value: "present", label: "Present" },
];

export const DEFAULT_FROM_VALUE: ExportDateFieldValue = {
  preset: "since_opening",
  customDate: "",
};

export const DEFAULT_TO_VALUE: ExportDateFieldValue = {
  preset: "present",
  customDate: "",
};

/** Today's date as YYYY-MM-DD (used to cap the date pickers to today). */
export const todayApiDate = (): string => moment().format(API_FORMAT);

/**
 * Resolve a date field to the YYYY-MM-DD value sent to the API. Open-ended
 * selections ("Account Open" / "Present") return `undefined`, which the API
 * treats as no bound.
 */
export const resolveApiDate = (
  value: ExportDateFieldValue
): string | undefined => {
  switch (value.preset) {
    case "since_opening":
    case "present":
      return undefined;
    case "today":
      return moment().format(API_FORMAT);
    case "past_24_hours":
      return moment().subtract(1, "days").format(API_FORMAT);
    case "past_7_days":
      return moment().subtract(7, "days").format(API_FORMAT);
    case "past_30_days":
      return moment().subtract(30, "days").format(API_FORMAT);
    case "past_90_days":
      return moment().subtract(90, "days").format(API_FORMAT);
    case "past_year":
      return moment().subtract(1, "year").format(API_FORMAT);
    case "custom": {
      const parsed = moment(value.customDate, API_FORMAT, true);
      return parsed.isValid() ? parsed.format(API_FORMAT) : undefined;
    }
    default:
      return undefined;
  }
};

/**
 * Human-readable label for the collapsed control / request summary. Custom
 * dates use the browser locale, matching how the native date input renders.
 */
export const displayLabelForField = (
  value: ExportDateFieldValue,
  options: DateDropdownOption[]
): string => {
  if (value.preset === "custom") {
    const parsed = moment(value.customDate, API_FORMAT, true);
    return parsed.isValid()
      ? parsed.toDate().toLocaleDateString()
      : "Custom Date Range";
  }
  const option = options.find((o) => o.value === value.preset);
  return option?.selectedLabel ?? option?.label ?? "";
};
